import * as Sentry from '@sentry/browser';
import { v4 as uuidv4 } from 'uuid';

import { chatClient, memberClient } from 'src/config/Apollo';
import { CREATE_NEW_MESSAGE, UPDATE_MESSAGE } from 'src/graphql/chat/mutations';
import { GET_MESSAGES, GET_PAGINATED_CONVERSATIONS } from 'src/graphql/chat/queries';
import { ON_NEW_MESSAGE } from 'src/graphql/chat/subscriptions';
import {
  ConversationsQueryResult,
  ConversationsQueryVars,
  Message,
  MessagesQueryResult,
  MessagesQueryVars,
  NewMessage,
} from 'src/models/Chat';
import {
  GetConversationsWithUnreadMessagesDocument,
  GetConversationsWithUnreadMessagesQuery,
  GetConversationsWithUnreadMessagesQueryVariables,
} from 'src/documents/GetUnreadConversations.generated';
import {
  GetMemberTopicsOfFocusDocument,
  GetMemberTopicsOfFocusQuery,
  GetMemberTopicsOfFocusQueryVariables,
} from 'src/components/MemberProfileDrawer/documents/GetMemberTopicsOfFocus.generated';

const FETCH_MESSAGES_AMOUNT = 10;

const paginatedConversationsDefaultVariables = {
  first: FETCH_MESSAGES_AMOUNT,
  active: true,
  orderBy: 'newmatch',
  memberName: '',
};

export class ChatService {
  public async fetchConversations(
    variables: ConversationsQueryVars = {},
  ): Promise<ConversationsQueryResult['conversations'] | null> {
    try {
      const { data } = await chatClient.query<ConversationsQueryResult>({
        query: GET_PAGINATED_CONVERSATIONS,
        fetchPolicy: 'network-only',
        variables: {
          ...paginatedConversationsDefaultVariables,
          ...variables,
        },
      });

      return data.conversations;
    } catch (e) {
      Sentry.configureScope((scope) => {
        scope.setTag('chat', 'error_fetching_conversations');
        Sentry.captureException(e);
      });
    }

    return null;
  }

  public async fetchAllUnreadConvos() {
    try {
      const { data } = await memberClient.query<
        GetConversationsWithUnreadMessagesQuery,
        GetConversationsWithUnreadMessagesQueryVariables
      >({
        query: GetConversationsWithUnreadMessagesDocument,
        fetchPolicy: 'network-only',
      });

      return (
        data.me?.conversationsWithUnreadMessages
          ?.map((conversation) => conversation?.uuid)
          .filter((uuid): uuid is string => uuid != null) ?? []
      );
    } catch (err) {
      Sentry.configureScope((scope) => {
        scope.setTag('chat', 'error trying to fetch the initial conversations for unread messages');
        Sentry.captureException(err);
      });

      return [];
    }
  }

  public async updateMessage(message: Message): Promise<void> {
    try {
      await chatClient.mutate({
        mutation: UPDATE_MESSAGE,
        variables: {
          messageUuid: message.uuid,
          conversationUuid: message.conversationUuid,
          readAt: new Date(),
        },
      });
    } catch (e) {
      Sentry.configureScope((scope) => {
        scope.setTag('chat', 'error trying to update read messages');
        Sentry.captureException(e);
      });
    }
  }

  public async fetchMessages(
    variables: MessagesQueryVars,
  ): Promise<MessagesQueryResult['getMessages'] | null> {
    try {
      const { data } = await chatClient.query<MessagesQueryResult>({
        query: GET_MESSAGES,
        fetchPolicy: 'network-only',
        variables: {
          first: FETCH_MESSAGES_AMOUNT,
          ...variables,
        },
      });

      return data.getMessages;
    } catch (e) {
      Sentry.configureScope((scope) => {
        scope.setTag('chat', 'error_fetching_messages');
        Sentry.captureException(e);
      });
    }

    return null;
  }

  public async createMessage(message: NewMessage): Promise<boolean> {
    try {
      const messageId = uuidv4();

      if (!message.chatMessage && !message.resourcesToAdd.length) {
        return false;
      }

      await chatClient.mutate({
        mutation: CREATE_NEW_MESSAGE,
        variables: {
          messageUuid: messageId,
          conversationUuid: message.conversationUuid,
          content: message.chatMessage,
          resources: message.resourcesToAdd.map(({ id, resourceType }) => {
            return { id, resourceType };
          }),
        },
      });

      return true;
    } catch (e) {
      Sentry.configureScope((scope) => {
        scope.setTag('chat', 'error_creating_message');
        Sentry.captureException(e);
      });
    }
    return false;
  }

  public checkUnreads(coachUuid: string, callback: Function): void {
    try {
      chatClient
        .subscribe({
          query: ON_NEW_MESSAGE,
        })
        .subscribe((data) => {
          const { conversationUuid = '', receiverUuid } = data?.data?.onNewMessage || {};

          if (receiverUuid === coachUuid) {
            callback(conversationUuid);
          }
        });
    } catch (err: any) {
      Sentry.configureScope((scope) => {
        scope.setTag('chat', 'error_in_unread_message_subscription');
        err.name = 'Error in ChatService>checkUnreads';
        Sentry.captureException(err);
      });
    }
  }

  public async fetchMemberTopicsOfFocus(memberUUID) {
    try {
      const { data } = await memberClient.query<
        GetMemberTopicsOfFocusQuery,
        GetMemberTopicsOfFocusQueryVariables
      >({
        query: GetMemberTopicsOfFocusDocument,
        fetchPolicy: 'network-only',
        variables: { uuid: memberUUID },
      });

      // const subtopicSelections = data.member?.topicSelectionGroup?.subtopicSelections.flatMap(
      //   (subTopic) => subTopic.map((x) => x.name),
      // );

      const subtopicSelections = data.member?.topicSelectionGroup?.subtopicSelections.map(
        (x) => x.subtopicOfFocus.name,
      );

      return subtopicSelections;
    } catch (err) {
      Sentry.configureScope((scope) => {
        scope.setTag('chat', 'error trying to fetch the members topics of focus');
        Sentry.captureException(err);
      });
    }

    return [];
  }
}
